import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { documentsApi, type DocRow } from '../features/documents/api.ts';
import { IosInstallHint } from '../components/IosInstallHint.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { useAuthStore } from '../store/auth.ts';
import { useSearchStore } from '../store/search.ts';
import { statusLabel } from '../lib/status.ts';
import { Icon } from '../components/Icon.tsx';

const PAGE_SIZE = 20;

type Tab = 'sent' | 'to-sign' | 'to-review' | 'completed' | 'all';
const BASE_TABS: Array<{ id: Tab; label: string }> = [
  { id: 'sent', label: 'Sent' },
  { id: 'to-sign', label: 'To sign' },
  // Documents assigned to this user for review (admin-configured review flow).
  { id: 'to-review', label: 'To review' },
  { id: 'completed', label: 'Completed' },
];
// Org admins get an extra org-wide tab listing every document in the org.
const ADMIN_TAB: { id: Tab; label: string } = { id: 'all', label: 'All' };

function statusPill(status: string): string {
  switch (status) {
    case 'DRAFT':     return 'pill pill-neutral';
    case 'IN_REVIEW': return 'pill pill-pending';
    case 'PENDING':   return 'pill pill-pending';
    case 'COMPLETED': return 'pill pill-success';
    case 'CANCELLED': return 'pill pill-neutral';
    case 'DECLINED':  return 'pill pill-danger';
    default:          return 'pill pill-neutral';
  }
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

// Animate a number from its previous displayed value to a new target, so the
// live stats "tick" up when the poll returns a higher figure.
function useCountUp(target: number, duration = 700): number {
  const [val, setVal] = useState(target);
  const valRef = useRef(target);
  useEffect(() => { valRef.current = val; }, [val]);
  useEffect(() => {
    const from = valRef.current;
    if (from === target) { setVal(target); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

// Org-wide impact banner: documents signed + estimated paper (MVR) saved.
// Polls every 15s so the figures stay live without a manual refresh.
function StatsBanner(): JSX.Element {
  const { data, isFetching } = useQuery({
    queryKey: ['doc-stats'],
    queryFn: () => documentsApi.stats(),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
  const signed = useCountUp(data?.signedCount ?? 0);
  const mvr = useCountUp(data?.mvrSaved ?? 0);
  const pages = data?.pagesSaved ?? 0;
  const rate = data?.mvrPerPage ?? 0.2;
  // Third card: how many documents are awaiting THIS user's signature.
  const { data: toSign } = useQuery({
    queryKey: ['docs-count', 'to-sign'],
    queryFn: () => documentsApi.list('to-sign', 1, 1),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  const awaiting = toSign?.total ?? 0;

  const card = 'shrink-0 min-w-[43%] snap-start rounded-2xl border border-black/[0.04] p-4 sm:min-w-0 sm:shrink sm:p-5';
  const chip = 'grid size-9 sm:size-10 shrink-0 place-items-center rounded-full';
  const label = 'text-[0.8rem] sm:text-sm font-medium text-foreground/70 leading-tight';
  const num = 'mt-3 text-[2rem] sm:text-[2.5rem] font-medium leading-none tabular text-ink';
  return (
    <section className="flex gap-3 -mx-4 px-4 pb-1 overflow-x-auto snap-x [scrollbar-width:none] sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible">
      <div className={`${card} bg-[hsl(var(--chrome))]`}>
        <div className="flex items-center gap-2.5">
          <span className={`${chip} bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]`}><Icon name="task_alt" size={20} /></span>
          <p className={label}>Documents signed</p>
        </div>
        <p className={num}>{Math.round(signed).toLocaleString()}</p>
      </div>
      <div className={`${card} bg-[hsl(var(--chrome))]`}>
        <div className="flex items-center gap-2.5">
          <span className={`${chip} bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))]`}><Icon name="eco" size={20} /></span>
          <p className={label}>Paper saved</p>
        </div>
        <p className={num}>MVR {mvr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        <p className="mt-2 text-xs text-foreground/60 hidden sm:block">
          ≈ {pages.toLocaleString()} pages · MVR {rate.toFixed(2)}/page · updates live{isFetching ? '…' : ''}
        </p>
      </div>
      <div className={`${card} bg-[hsl(var(--chrome))]`}>
        <div className="flex items-center gap-2.5">
          <span className={`${chip} bg-[hsl(var(--warning-container))] text-[hsl(var(--on-warning-container))]`}><Icon name="schedule" size={20} /></span>
          <p className={label}>Awaiting you</p>
        </div>
        <p className={num}>{awaiting}</p>
      </div>
    </section>
  );
}

export function Dashboard(): JSX.Element {
  const isOrgAdmin = useAuthStore((s) => s.user?.role === 'ORG_ADMIN');
  const TABS = isOrgAdmin ? [...BASE_TABS, ADMIN_TAB] : BASE_TABS;
  const [tab, setTab] = useState<Tab>('sent');
  const [page, setPage] = useState(1);
  // Two values: `query` is what the input shows (live), `debounced` is what
  // actually drives the request. Avoids firing a query on every keystroke.
  const query = useSearchStore((s) => s.query);
  const setQuery = useSearchStore((s) => s.setQuery);
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(query.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isLoading } = useQuery({
    queryKey: ['docs', tab, page, debounced],
    queryFn: () => documentsApi.list(tab, page, PAGE_SIZE, debounced || undefined),
  });
  const docs = data?.items ?? [];
  const hasSearch = debounced.length > 0;

  // Pending badges: does this user have documents awaiting their signature or
  // review? A red dot on the "To sign" / "To review" tabs when so. Polled so it
  // stays live; pageSize 1 keeps it cheap (we only read `total`).
  const { data: toSignCount } = useQuery({
    queryKey: ['docs-count', 'to-sign'],
    queryFn: () => documentsApi.list('to-sign', 1, 1),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  const { data: toReviewCount } = useQuery({
    queryKey: ['docs-count', 'to-review'],
    queryFn: () => documentsApi.list('to-review', 1, 1),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  const pendingByTab: Partial<Record<Tab, number>> = {
    'to-sign': toSignCount?.total ?? 0,
    'to-review': toReviewCount?.total ?? 0,
  };

  function changeTab(next: Tab): void {
    setTab(next);
    setPage(1);
  }

  return (
    <div className="page space-y-5 md:space-y-8">
      <IosInstallHint />

      <header className="hidden md:block">
        <p className="text-sm font-medium text-muted-foreground">Workspace</p>
        <h1 className="font-sans font-normal text-[2.25rem] md:text-[2.75rem] leading-tight mt-1">
          Documents
        </h1>
      </header>

      <StatsBanner />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div role="tablist" className="flex gap-2 flex-nowrap overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => changeTab(t.id)}
              className={[
                'shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors border inline-flex items-center gap-1.5',
                tab === t.id
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] border-transparent'
                  : 'text-muted-foreground border-border hover:bg-muted/60',
              ].join(' ')}
            >
              {t.label}
              {(pendingByTab[t.id] ?? 0) > 0 && (
                <span
                  aria-label={`${pendingByTab[t.id]} pending`}
                  className="size-2 shrink-0 rounded-full bg-destructive"
                />
              )}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents"
            aria-label="Search documents"
            className="w-full rounded-full bg-[hsl(var(--chrome))] pl-10 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground/80 outline-none focus:ring-2 focus:ring-ring/30 transition"
          />
        </div>
      </div>

      {isLoading && (
        <p role="status" aria-live="polite" className="status-label">Loading documents…</p>
      )}

      {!isLoading && docs.length === 0 && (
        <div className="card-paper p-10 text-center space-y-3">
          <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
            <Icon name={hasSearch ? 'search' : 'description'} size={22} />
          </div>
          {hasSearch ? (
            <>
              <p className="font-serif text-2xl text-ink">No matches.</p>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Nothing in <i>{TABS.find((t) => t.id === tab)?.label}</i> matches
                <span className="text-foreground"> “{debounced}”</span>. Try a different word or switch tabs.
              </p>
            </>
          ) : (
            <>
              <p className="font-serif text-2xl text-ink">Nothing here yet.</p>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {tab === 'sent' && 'Send a PDF and place signature boxes — recipients sign in their browser.'}
                {tab === 'to-sign' && 'Documents others send you will appear here.'}
                {tab === 'to-review' && 'Documents assigned to you for approval will appear here.'}
                {tab === 'completed' && 'Signed documents you sent or signed will collect here.'}
                {tab === 'all' && 'Every document across your organisation will appear here.'}
              </p>
            </>
          )}
        </div>
      )}

      {!isLoading && docs.length > 0 && (
        <div className="space-y-2">
          <ul className="rounded-3xl bg-[hsl(var(--chrome))] overflow-hidden">
            {docs.map((d: DocRow) => (
              <li key={d.id} className="border-b border-black/[0.05] last:border-b-0">
                <Link
                  to="/documents/$id"
                  params={{ id: d.id }}
                  className="flex items-center gap-4 px-4 sm:px-5 py-4 transition-colors hover:bg-[hsl(var(--surface-high))]"
                >
                  <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))]">
                    <Icon name="description" size={22} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[0.95rem] font-medium text-ink truncate">{d.title}</div>
                    <div className="text-sm text-muted-foreground truncate">{d.sender.name}</div>
                  </div>
                  <span className={statusPill(d.status)}>{statusLabel(d.status)}</span>
                  <div className="hidden sm:block text-xs text-muted-foreground shrink-0 tabular w-20 text-right">
                    {fmtDate(d.sentAt ?? d.createdAt)}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <Pagination
            page={data?.page ?? 1}
            pageSize={data?.pageSize ?? PAGE_SIZE}
            total={data?.total ?? 0}
            onChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
